import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Lock,
  Download,
  FileText,
  ShieldCheck,
  Crown,
  Settings,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMAIL_KEY = "nc_pro_email";

const PERKS = [
  "Industry guides — publishing, royalties & registration roadmaps",
  "Studio-ready templates — split sheets, checklists & session docs",
  "Press / EPK assets for pitching & placements",
  "New resources added regularly — yours while you're a member",
];

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * The Pro Vault membership UI as a self-contained section, embedded on the
 * Backstage page. Handles its own Stripe-return query params (pv_status) so it
 * never collides with Backstage's own `email` param.
 */
export function ProVaultPanel() {
  const [params, setParams] = useSearchParams();
  const status = params.get("pv_status");
  const sessionId = params.get("session_id");

  const [price, setPrice] = useState(6.5);
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem(EMAIL_KEY) || "";
    } catch {
      return "";
    }
  });
  const [name, setName] = useState("");
  const [member, setMember] = useState(null); // { name, items: [...] } when unlocked
  const [checking, setChecking] = useState(false);
  const [joining, setJoining] = useState(false);

  const loadPublic = useCallback(async () => {
    try {
      const [s, it] = await Promise.all([
        api.get("/public/pro-vault/settings"),
        api.get("/public/pro-vault/items"),
      ]);
      if (typeof s.data.price_usd === "number") setPrice(s.data.price_usd);
      setItems(it.data.items || []);
    } catch {
      /* non-fatal */
    }
  }, []);

  const checkAccess = useCallback(async (em, { quiet = false } = {}) => {
    if (!em) return false;
    setChecking(true);
    try {
      const { data } = await api.post("/public/pro-vault/access", { email: em.trim() });
      if (data.access) {
        setMember({ name: data.name, items: data.items || [] });
        try {
          localStorage.setItem(EMAIL_KEY, em.trim());
        } catch {
          /* ignore */
        }
        return true;
      }
      setMember(null);
      if (!quiet) toast.message(data.message || "No active membership for that email.");
      return false;
    } catch (err) {
      if (!quiet) toast.error(formatApiError(err?.response?.data?.detail) || "Could not verify membership.");
      return false;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    let saved = "";
    try {
      saved = localStorage.getItem(EMAIL_KEY) || "";
    } catch {
      saved = "";
    }
    const id = setTimeout(() => {
      loadPublic();
      if (saved) checkAccess(saved, { quiet: true });
    }, 0);
    return () => clearTimeout(id);
  }, [loadPublic, checkAccess]);

  // Return-from-Stripe: confirm subscription, then unlock
  useEffect(() => {
    if (status === "success" && sessionId) {
      toast.loading("Confirming your membership…", { id: "pv-poll" });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/public/pro-vault/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Welcome to the Pro Vault. Everything's unlocked. 👑", { id: "pv-poll" });
            if (data.email) {
              setEmail(data.email);
              await checkAccess(data.email, { quiet: true });
            }
            const np = new URLSearchParams(params);
            np.delete("pv_status");
            np.delete("session_id");
            setParams(np, { replace: true });
            return;
          }
          if (attempts < 6) setTimeout(poll, 2000);
          else toast.message("Still finalizing — refresh in a moment.", { id: "pv-poll" });
        } catch {
          if (attempts < 6) setTimeout(poll, 2000);
          else toast.dismiss("pv-poll");
        }
      };
      poll();
    } else if (status === "cancelled") {
      toast("Checkout cancelled — no charge.");
      const np = new URLSearchParams(params);
      np.delete("pv_status");
      setParams(np, { replace: true });
    }
  }, [status, sessionId]);

  const join = async () => {
    if (!email.trim()) {
      toast.error("Enter your email to continue.");
      return;
    }
    setJoining(true);
    try {
      const { data } = await api.post("/public/pro-vault/checkout", {
        email: email.trim(),
        name: name.trim() || undefined,
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't start checkout. Please try again.");
      setJoining(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/public/pro-vault/portal", {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't open the billing portal.");
    }
  };

  const download = (item) => {
    if (item.downloads_remaining <= 0) {
      toast.error("You've hit the download limit for this file.");
      return;
    }
    const url = `${BACKEND}/api/public/pro-vault/download/${item.id}?email=${encodeURIComponent(email.trim())}`;
    window.location.assign(url);
    toast.success(`Downloading “${item.title}”…`);
    setTimeout(() => checkAccess(email, { quiet: true }), 2500);
  };

  const displayItems = member ? member.items : items;

  return (
    <div data-testid="pro-vault-panel">
      {/* Section header */}
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[color:var(--gold)] mb-5">
          <ShieldCheck size={14} strokeWidth={1.4} /> For Artists &amp; Industry Pros
        </div>
        <h2 className="font-serif font-light tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
          The <em className="italic" style={{ color: "var(--gold)" }}>Pro Vault</em>
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-base text-white/65 leading-relaxed">
          Nina&apos;s working library for the music business — the guides, templates, and
          registration roadmaps she built protecting her own catalog. Unlock all of it.
        </p>
        <div className="mt-6 inline-flex items-baseline gap-2">
          <span className="font-serif text-4xl" style={{ color: "var(--gold)" }}>${price.toFixed(2)}</span>
          <span className="text-white/50 text-sm uppercase tracking-[0.2em]">/ month</span>
        </div>
      </div>

      {/* Member banner OR join card */}
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-10">
        {member ? (
          <div className="surface p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="pro-vault-member-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} style={{ color: "var(--gold)" }} />
              <div>
                <p className="text-sm">
                  Membership active{member.name ? ` · ${member.name}` : ""} 👑
                </p>
                <p className="text-[11px] text-white/45">{email}</p>
              </div>
            </div>
            <button
              onClick={openPortal}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-[color:var(--gold)] transition-colors"
              data-testid="pro-vault-manage"
            >
              <Settings size={14} /> Manage / Cancel
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="surface p-8">
              <h3 className="font-serif text-2xl font-light mb-5">What&apos;s inside</h3>
              <ul className="space-y-3">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-white/70">
                    <Sparkles size={15} className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="surface p-8 flex flex-col" data-testid="pro-vault-join-card">
              <h3 className="font-serif text-2xl font-light mb-2">Get the keys</h3>
              <p className="text-sm text-white/55 mb-6">
                ${price.toFixed(2)}/mo · cancel anytime. Already a member? Enter your email to unlock.
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full bg-transparent border border-white/15 rounded-none px-4 h-12 text-sm outline-none focus:border-[color:var(--gold)] transition-colors"
                  data-testid="pro-vault-name"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-transparent border border-white/15 rounded-none px-4 h-12 text-sm outline-none focus:border-[color:var(--gold)] transition-colors"
                  data-testid="pro-vault-email"
                />
              </div>
              <div className="mt-5 flex flex-col gap-3">
                <button
                  onClick={join}
                  disabled={joining}
                  className="btn-gold rounded-none h-12 text-xs uppercase tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="pro-vault-subscribe"
                >
                  <Crown size={14} />
                  {joining ? "Starting checkout…" : `Become a Member · $${price.toFixed(2)}/mo`}
                </button>
                <button
                  onClick={() => checkAccess(email)}
                  disabled={checking}
                  className="text-[11px] uppercase tracking-[0.25em] text-white/55 hover:text-[color:var(--gold)] transition-colors disabled:opacity-50"
                  data-testid="pro-vault-unlock"
                >
                  {checking ? "Checking…" : "I'm already a member — unlock"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resource library */}
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-14">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h3 className="font-serif text-2xl lg:text-3xl font-light tracking-tight">The Library</h3>
          <span className="text-[11px] uppercase tracking-[0.25em] text-white/40">
            {displayItems.length} resource{displayItems.length === 1 ? "" : "s"}
          </span>
        </div>

        {displayItems.length === 0 ? (
          <p className="text-white/45 py-10">Resources are being added — check back soon.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayItems.map((item) => (
              <div key={item.id} className="vault-item surface overflow-hidden flex flex-col" data-testid={`vault-item-${item.slug}`}>
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#2a1822] to-[#0a0508]">
                  {item.cover_image ? (
                    <img
                      src={imgUrl(item.cover_image)}
                      alt={item.title}
                      loading="lazy"
                      className={`w-full h-full object-cover transition duration-500 ${member ? "" : "blur-[6px] scale-105 opacity-70"}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText size={40} className="text-white/20" />
                    </div>
                  )}
                  {!member && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm border border-[color:var(--gold)]/40 flex items-center justify-center">
                        <Lock size={18} style={{ color: "var(--gold)" }} />
                      </div>
                    </div>
                  )}
                  <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 bg-black/55 backdrop-blur-sm border border-white/15 text-white/70">
                    {item.category}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h4 className="font-serif text-lg leading-snug mb-2">{item.title}</h4>
                  {item.description && (
                    <p className="text-[13px] text-white/55 leading-relaxed line-clamp-3 mb-4">{item.description}</p>
                  )}
                  <div className="mt-auto pt-2">
                    {member ? (
                      <button
                        onClick={() => download(item)}
                        disabled={item.downloads_remaining <= 0}
                        className="w-full btn-gold rounded-none h-11 text-[11px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid={`vault-download-${item.slug}`}
                      >
                        <Download size={13} />
                        {item.downloads_remaining <= 0
                          ? "Limit reached"
                          : `Download${item.file_ext ? ` (${item.file_ext.toUpperCase()})` : ""}`}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/40">
                        <Lock size={12} /> Members only
                        {item.file_size ? <span className="ml-auto normal-case tracking-normal text-white/30">{fmtSize(item.file_size)}</span> : null}
                      </div>
                    )}
                    {member && (
                      <p className="text-[10px] text-white/35 mt-2 text-center">
                        {item.downloads_remaining} of {item.max_downloads} downloads left
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProVaultPanel;
